/**
 * Convenção única de query keys do TanStack Query.
 *
 * Cada domínio expõe um objeto de fábricas de chave, sempre como tupla
 * começando pelo nome do domínio. Isso permite invalidar tudo de um domínio
 * (`queryClient.invalidateQueries({ queryKey: queryKeys.participants.all })`)
 * ou só uma entidade específica (`queryKeys.participants.detail(id)`).
 *
 * Regra: toda key é `as const` (tuplas literais), nunca array solto — isso
 * é o que dá ao TanStack Query a granularidade de invalidação por prefixo.
 */
export const queryKeys = {
  auth: {
    me: ["auth", "me"] as const,
  },
  events: {
    all: ["events"] as const,
    active: ["events", "active"] as const,
    detail: (eventId: number | string) => ["events", "detail", eventId] as const,
  },
  dashboard: {
    summary: (eventId: number | string) => ["dashboard", eventId, "summary"] as const,
    kpis: (eventId: number | string) => ["dashboard", eventId, "kpis"] as const,
    timeline: (eventId: number | string) => ["dashboard", eventId, "timeline"] as const,
  },
  participants: {
    all: ["participants"] as const,
    list: (eventId: number | string) => ["participants", "list", eventId] as const,
  },
  sessions: {
    polls: (sessionId: number | string) => ["sessions", sessionId, "polls"] as const,
    questions: (sessionId: number | string) => ["sessions", sessionId, "questions"] as const,
    conteudos: (sessionId: number | string) => ["sessions", sessionId, "conteudos"] as const,
    eventPolls: (eventId: number | string) => ["sessions", "event", eventId, "polls"] as const,
  },
  polls: {
    results: (pollId: number | string) => ["polls", pollId, "results"] as const,
    wordcloud: (pollId: number | string) => ["polls", pollId, "wordcloud"] as const,
  },
  knowledge: {
    /** GET /api/kb não aceita filtro — sempre a mesma key, filtragem é client-side. */
    list: ["knowledge", "list"] as const,
  },
  support: {
    /** Sem filtro por query — o escopo vem da sessão do usuário logado. */
    queue: ["support", "queue"] as const,
    stats: ["support", "stats"] as const,
    ticket: (ticketId: number | string) => ["support", "ticket", ticketId] as const,
  },
  certificates: {
    list: (eventId: number | string) => ["certificates", "list", eventId] as const,
  },
  leads: {
    all: ["leads"] as const,
    list: (eventId: number | string) => ["leads", "list", eventId] as const,
    byExhibitorCode: (code: string) => ["leads", "byExhibitorCode", code] as const,
  },
  matchmaking: {
    byCode: (code: string) => ["matchmaking", "byCode", code] as const,
    mineByCode: (code: string) => ["matchmaking", "mineByCode", code] as const,
  },
  users: {
    all: ["users"] as const,
  },
  whatsapp: {
    quality: ["whatsapp", "quality"] as const,
    templates: ["whatsapp", "templates"] as const,
    window: (phone: string) => ["whatsapp", "window", phone] as const,
  },
  audit: {
    accessLog: (limit?: number, offset?: number) =>
      ["audit", "accessLog", limit ?? 200, offset ?? 0] as const,
  },
  health: {
    check: ["health"] as const,
  },
} as const;
